import { useState, useEffect } from "react";
import { CheckCircle2, XCircle, Search, ShieldCheck, Clock, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
      timeZoneName: "short",
    });
  } catch { return iso; }
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
};

const WaiverRow = ({ student }) => {
  const signed = student.waiver_signed;
  return (
    <div
      className={`brutalist-card p-4 flex flex-wrap items-start gap-4 ${signed ? "" : "border-[#FF9800]"}`}
      data-testid={`waiver-row-${student.student_id}`}
    >
      {/* Avatar */}
      <div className={`w-10 h-10 rounded-full border-2 border-[#0A0A0A] flex items-center justify-center font-bold text-sm shrink-0 ${signed ? "bg-[#D6E6FA]" : "bg-[#FFF3E0]"}`}>
        <User className="w-4 h-4" />
      </div>

      {/* Student info */}
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate">{student.student_name}</div>
        <div className="text-xs text-[#4A4A4A] truncate">{student.student_email}</div>
        {student.age_range && (
          <div className="text-xs text-[#4A4A4A]">Age: {student.age_range}</div>
        )}
        <div className="text-xs text-[#4A4A4A] mt-0.5">
          Registered: {formatDate(student.created_at)}
        </div>
      </div>

      {/* Waiver status */}
      <div className="shrink-0 text-right">
        {signed ? (
          <div data-testid={`waiver-signed-${student.student_id}`}>
            <div className="flex items-center justify-end gap-1.5 text-[#2E7D32] font-bold text-sm mb-1">
              <CheckCircle2 className="w-4 h-4" /> Signed
            </div>
            {student.waiver_signed_at ? (
              <div className="flex items-center justify-end gap-1 text-xs text-[#4A4A4A]">
                <Clock className="w-3 h-3" />
                {formatDateTime(student.waiver_signed_at)}
              </div>
            ) : (
              <div className="text-xs text-[#4A4A4A] italic">Signed (date unknown)</div>
            )}
            {student.waiver_signature && (
              <div className="mt-1 text-xs border border-[#0A0A0A]/20 rounded px-2 py-0.5 bg-[#F0EFEB] inline-block font-medium italic">
                "{student.waiver_signature}"
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1.5 text-[#E65100] font-bold text-sm" data-testid={`waiver-unsigned-${student.student_id}`}>
            <XCircle className="w-4 h-4" /> Not signed
          </div>
        )}
      </div>
    </div>
  );
};

const AdminWaiverHistory = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | signed | unsigned

  useEffect(() => { loadLog(); }, []);

  const loadLog = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/admin/waiver-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(res.data);
    } catch {
      toast.error("Failed to load waiver history");
    } finally {
      setLoading(false);
    }
  };

  const signedCount = records.filter(r => r.waiver_signed).length;
  const unsignedCount = records.length - signedCount;

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = r.student_name.toLowerCase().includes(q) || r.student_email.toLowerCase().includes(q);
    const matchFilter =
      filter === "all" ? true :
      filter === "signed" ? r.waiver_signed :
      !r.waiver_signed;
    return matchSearch && matchFilter;
  });

  return (
    <div data-testid="admin-waiver-history">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-6 h-6 text-[#3C8DE4]" />
            <h1 className="text-2xl font-extrabold">Social Media Waiver Log</h1>
          </div>
          <p className="text-sm text-[#4A4A4A]">
            Timestamped record of every student's social media waiver status
          </p>
        </div>
        <button
          onClick={loadLog}
          className="brutalist-btn-primary px-4 py-2 text-sm"
          data-testid="refresh-waiver-btn"
        >
          Refresh
        </button>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-[#0A0A0A] bg-white text-sm font-bold">
          <CheckCircle2 className="w-4 h-4 text-[#2E7D32]" />
          {signedCount} signed
        </div>
        {unsignedCount > 0 && (
          <div className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-[#FF9800] bg-[#FFF3E0] text-sm font-bold text-[#E65100]" data-testid="unsigned-summary">
            <XCircle className="w-4 h-4" />
            {unsignedCount} not yet signed
          </div>
        )}
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A4A4A]" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="brutalist-input w-full pl-9"
            data-testid="waiver-search"
          />
        </div>
        <div className="flex gap-2">
          {[
            { key: "all", label: "All" },
            { key: "unsigned", label: "Not Signed" },
            { key: "signed", label: "Signed" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-2 rounded-lg border-2 border-[#0A0A0A] text-sm font-bold transition-colors ${
                filter === key ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F0EFEB]"
              }`}
              data-testid={`waiver-filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-[#4A4A4A]" data-testid="waiver-loading">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#4A4A4A]" data-testid="waiver-empty">
          {search || filter !== "all" ? "No students match your filter." : "No students found."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <WaiverRow key={r.student_id} student={r} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminWaiverHistory;
